import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyType, Language } from '@app/models/admin.model';
import {
  ActiveLanguagesForm,
  CompanyTypeFormGroup,
  MergeLanguagesForm,
} from '@app/models/form.models';
import { CompanyTypeSaveService } from '@app/services/admin/company-type/company-type-save.service';
import { MasterdataService } from '@app/services/admin/masterdata.service';
import { GenericDataService } from '@app/services/generic-data.service';
import { ToastService } from '@app/services/toast.service';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin } from 'rxjs';
import { CountriesUsedTabComponent } from './countries-used-tab/countries-used-tab.component';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { LocalizationTabComponent } from './localization-tab/localization-tab.component';

type Tab = 'details' | 'localizations' | 'countriesUsed';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-company-type-details',
  standalone: true,
  templateUrl: './company-type-details.component.html',
  styleUrl: './company-type-details.component.scss',
  imports: [
    CommonModule,
    DetailsTabComponent,
    LocalizationTabComponent,
    CountriesUsedTabComponent,
    DetailsPageHeaderComponent,
    TranslatePipe,
  ],
})
export class CompanyTypeDetailsComponent implements OnInit {
  private translate = inject(TranslateService);

  companyTypeId: string | null = null;
  companyTypeSignal = signal<CompanyType | null>(null);

  isLoading = true;

  activeTab: Tab = 'details';
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.common.tabs.details' },
    { key: 'localizations', label: 'admin.common.tabs.localizations' },
    { key: 'countriesUsed', label: 'admin.companyType.tabs.countriesUsed' },
  ];

  companyTypeFormGroup: FormGroup<CompanyTypeFormGroup>;
  activeLanguagesForm: FormArray<FormGroup<ActiveLanguagesForm>> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: GenericDataService,
    private masterdataService: MasterdataService,
    private toast: ToastService,
    private fb: FormBuilder,
    private saveSvc: CompanyTypeSaveService
  ) {
    const minOneCountry: ValidatorFn = (g: AbstractControl) => {
      const arr = (g.get('countries')?.value as string[]) ?? [];
      return arr.length > 0 ? null : { minOneCountry: true };
    };
    const localizationTabForm = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesForm>;
    }>({});
    this.companyTypeFormGroup = this.fb.group({
      details: this.fb.nonNullable.group({
        code: this.fb.nonNullable.control('', [
          Validators.required,
          Validators.maxLength(10),
        ]),
        description: this.fb.control('', [Validators.required]),
      }),
      countriesUsed: this.fb.group(
        {
          countries: this.fb.nonNullable.control<string[]>([]),
        },
        { validators: [minOneCountry] }
      ),
      localizationsTab: localizationTabForm,
    });

    this.activeLanguagesForm = this.fb.array<FormGroup<ActiveLanguagesForm>>(
      []
    );
  }

  ngOnInit(): void {
    this.companyTypeId = this.route.snapshot.paramMap.get('id');
    if (!this.companyTypeId) return this.goBack();

    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin({
      languages: this.service.loadLanguages(),
      companyType: this.masterdataService.getCompanyTypeById(
        this.companyTypeId!
      ),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ languages, companyType }) => {
          this.companyTypeSignal.set(companyType);

          const localizationRecord = this.fb.record<FormControl<string>>({});
          Object.entries(companyType.localized ?? {}).forEach(
            ([key, value]) => {
              localizationRecord.addControl(
                key,
                this.fb.nonNullable.control(String(value ?? ''))
              );
            }
          );

          const localizationTypeRecord = this.fb.record<FormControl<string>>(
            {}
          );
          Object.entries(companyType.localizedType ?? {}).forEach(
            ([key, value]) => {
              localizationTypeRecord.addControl(
                key,
                this.fb.nonNullable.control(String(value ?? ''))
              );
            }
          );

          this.companyTypeFormGroup.patchValue({
            details: {
              code: companyType.code,
              description: companyType.description,
            },
            countriesUsed: {
              countries: companyType.countriesUsed,
            },
          });

          this.activeLanguagesForm?.clear();
          languages.forEach((lang) => {
            this.activeLanguagesForm?.push(
              this.fb.group({
                code: this.fb.nonNullable.control(lang.code),

                description: this.fb.nonNullable.control(lang.description),
              })
            );
          });

          const merged = this.mergeLanguages(
            languages,
            companyType.localized ?? {},
            companyType.localizedType ?? {}
          );
          this.buildLanguagesForm(merged);
        },
        error: () => {},
      });
  }

  private mergeLanguages(
    languages: Language[],
    localized: Record<string, string>,
    localizedType: Record<string, string>
  ) {
    return languages.map((lang) => ({
      language: lang,
      localizedName: localized[lang.description] ?? '',
      localizedTypeName: localizedType[lang.description] ?? '',
    }));
  }

  private buildLanguagesForm(
    merged: {
      language: Language;
      localizedName: string;
      localizedTypeName: string;
    }[]
  ) {
    const formGroup = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesForm>;
    }>({});

    for (const item of merged) {
      const isEN = item.language.code === 'EN';
      formGroup.addControl(
        item.language.code,
        this.fb.group({
          localizedName: this.fb.nonNullable.control(
            item.localizedName,
            isEN ? [Validators.required] : []
          ),
          localizedTypeName: this.fb.nonNullable.control(
            item.localizedTypeName,
            isEN ? [Validators.required] : []
          ),
        })
      );
    }

    this.companyTypeFormGroup.setControl('localizationsTab', formGroup);
  }

  get countriesUsedList(): string[] {
    return this.companyTypeFormGroup.controls.countriesUsed.controls.countries
      .value;
  }

  saveCompanyType(): void {
    if (!this.companyTypeSignal()) return;

    if (this.companyTypeFormGroup.invalid) {
      this.companyTypeFormGroup.markAllAsTouched();
      this.companyTypeFormGroup.updateValueAndValidity({ emitEvent: true });
      return;
    }

    this.saveSvc.saveFromForms(
      this.companyTypeFormGroup,
      this.companyTypeSignal()
    );
  }

  deleteCompanyType(): void {
    if (!this.companyTypeId) return;
    this.isLoading = true;
    this.masterdataService
      .deleteCompanyTypeById(this.companyTypeId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.companyType.toasts.delete')
          );
          this.goBack();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('admin.companyType.toasts.error')
          );
        },
      });
  }

  setTab(tab: Tab): void {
    this.activeTab = tab;
  }

  goBack(): void {
    const target = history.state?.from || '/admin/database/companyType';
    this.router.navigate([target], { relativeTo: this.route });
  }
}
